const questionCount = 8;

// Preserve the legacy site's displayed results until the content owner approves
// a change to the questionnaire's scoring and wording.
export function calculateStopBangRisk(answers, calculatedBmi) {
    if (!Array.isArray(answers) || answers.length !== questionCount || answers.some((answer) => typeof answer !== 'boolean')) {
        throw new TypeError('Eight yes/no answers are required.');
    }

    const score = answers.filter(Boolean).length;
    const stopScore = answers.slice(0, 4).filter(Boolean).length;
    if (stopScore < 2) {
        return {
            risk: 'low',
            score,
            explanation: 'You have answered yes to fewer than 2 questions.',
            message: 'Good news! Your answers do not indicate sufficient risk for sleep apnea. If you still think you might be suffering from a sleep disorder,',
        };
    }

    let risk = 'intermediate';
    let explanation = 'You have answered yes to 3-4 questions.';
    if (answers[7]) {
        risk = 'high';
        explanation = 'You have answered yes to 2 or more stop questions + male gender.';
    }
    if (score >= 5) {
        risk = 'high';
        explanation = 'You have answered yes to 5 or more questions.';
    }
    if (answers[6]) {
        risk = 'high';
        explanation = 'You have answered yes to 2 or more STOP questions + neck circumference 17 inches / 43cm in male or 16 inches / 41cm in female.';
    }
    if (answers[4] || calculatedBmi >= 35) {
        risk = 'high';
        explanation = `You have answered yes to 2 or more STOP questions + BMI of ${calculatedBmi === undefined ? '>= 35' : calculatedBmi}`;
    }

    return {
        risk,
        score,
        explanation,
        // The old page kept this headline even when it set risk to "high".
        message: 'You are at Intermediate Risk for Obstructive Sleep Apnea (OSA)',
    };
}

function initStopBang(root) {
    const panels = [...root.querySelectorAll('[data-stopbang-panel]')];
    const letters = [...root.querySelectorAll('[data-stopbang-step]')];
    const answers = Array(questionCount).fill(null);
    let calculatedBmi;
    let current = 0;
    let complete = false;

    function showPanel(step) {
        current = step;
        root.dataset.stopbangStep = String(step);
        panels.forEach((panel) => {
            panel.hidden = Number(panel.dataset.stopbangPanel) !== step;
        });
        letters.forEach((letter, index) => {
            const question = index + 1;
            const active = question === step;
            letter.classList.toggle('is-active', active);
            letter.classList.toggle('is-answered', answers[index] !== null);
            letter.setAttribute('aria-current', active ? 'step' : 'false');
            letter.disabled = complete || step === 0 || (answers[index] === null && !active);
        });
        root.querySelectorAll('[data-stopbang-answer]').forEach((button) => {
            const question = Number(button.dataset.stopbangQuestion) - 1;
            button.setAttribute('aria-pressed', String(answers[question] === (button.dataset.stopbangAnswer === 'yes')));
        });
        if (step > 0 && step < 9) {
            panels[step].querySelector('.stopbang__prompt')?.setAttribute('tabindex', '-1');
            panels[step].querySelector('.stopbang__prompt')?.focus({ preventScroll: true });
        } else if (step === 9) {
            root.querySelector('[data-stopbang-result]')?.focus({ preventScroll: true });
        }
    }

    root.querySelector('[data-stopbang-start]')?.addEventListener('click', () => showPanel(1));

    root.querySelectorAll('[data-stopbang-answer]').forEach((button) => {
        button.addEventListener('click', () => {
            const question = Number(button.dataset.stopbangQuestion);
            answers[question - 1] = button.dataset.stopbangAnswer === 'yes';
            if (question < questionCount) {
                showPanel(question + 1);
            } else {
                const result = calculateStopBangRisk(answers, calculatedBmi);
                root.querySelector('[data-stopbang-explanation]').textContent = result.explanation;
                root.querySelector('[data-stopbang-result]').textContent = result.message;
                root.dataset.stopbangRisk = result.risk;
                complete = true;
                showPanel(9);
            }
        });
    });

    letters.forEach((letter) => {
        letter.addEventListener('click', () => {
            if (!complete && !letter.disabled) {
                showPanel(Number(letter.dataset.stopbangStep));
            }
        });
    });

    root.querySelector('[data-stopbang-bmi-form]')?.addEventListener('submit', (event) => {
        event.preventDefault();
        const form = event.currentTarget;
        if (!form.reportValidity()) return;
        const height = Number.parseInt(form.elements.height.value, 10);
        const weight = Number.parseInt(form.elements.weight.value, 10);
        if (!Number.isFinite(height) || !Number.isFinite(weight) || height <= 0 || weight <= 0) return;
        calculatedBmi = Math.ceil((weight / height / height) * 750);
        const output = root.querySelector('[data-stopbang-bmi-result]');
        output.textContent = `Your BMI: ${calculatedBmi}`;
        output.classList.toggle('is-above-threshold', calculatedBmi >= 35);
    });

    root.querySelector('[data-stopbang-restart]')?.addEventListener('click', () => {
        answers.fill(null);
        calculatedBmi = undefined;
        complete = false;
        delete root.dataset.stopbangRisk;
        root.querySelector('[data-stopbang-bmi-form]')?.reset();
        const bmiOutput = root.querySelector('[data-stopbang-bmi-result]');
        bmiOutput.textContent = '';
        bmiOutput.classList.remove('is-above-threshold');
        showPanel(0);
    });

    showPanel(0);
}

if (typeof document !== 'undefined') {
    document.querySelectorAll('[data-stopbang]').forEach(initStopBang);
}
